import { ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useCurrentPartner } from 'services/repositories/partners/partners';
import { useRelationship } from 'services/repositories/relationships/relationships';
import { getTradePartner } from 'support/helpers/relationships/relationships';
import { Connectors, RelationshipStatus, RelationshipV2 } from 'support/types';
import { useConnectionNavigation } from 'pages/Connections/ConnectionDetailsPage/contexts/ConnectionNavigationContext';
import { isReceiver } from 'pages/Connections/ConnectionDetailsPage/RelationshipDetailsPage/components/RelationshipDetailsBanner/helpers';
import { SlideOver, SlideOverSubtitleSkeleton, SlideOverTitleSkeleton } from 'components/Display/SlideOver/SlideOver';
import { PartnerLogo } from 'components/PartnerLogo/PartnerLogo';

import { ConnectionsTestNonWebEDI } from './components/ConnectionTestNonWebEDI/ConnectionsTestNonWebEDI';
import { ConnectionsTestWebEdi } from './components/ConnectionTestWebEDI/ConnectionsTestWebEDI';
import { TestMessagePendingReview } from './components/TestMessagePendingReview/TestMessagePendingReview';

export const ConnectionsTestSlideOver = ({ open, children }: { open: boolean; children?: ReactNode }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { partnerId, relationshipId } = useParams<{ partnerId: string; relationshipId: string }>();
  const { routeTo } = useConnectionNavigation();

  const { data: currentPartner, isLoading: isCurrentPartnerLoading } = useCurrentPartner();
  const { data: relationship, isLoading: isLoadingRelationship } = useRelationship({
    variables: {
      relationshipId,
    },
    keepPreviousData: true,
    enabled: Boolean(relationshipId),
  });

  const isLoading = isCurrentPartnerLoading || isLoadingRelationship;
  const otherPartner = relationship && currentPartner && getTradePartner(relationship, currentPartner.id);

  let slideOverContent: ReactNode = null;

  if (relationship) {
    switch (relationship.status) {
      case RelationshipStatus.READY_FOR_TESTING:
      case RelationshipStatus.HAS_TEST_MESSAGES:
        slideOverContent =
          relationship.sender_partner.id === currentPartner?.id ? (
            <TestMessageRequested relationship={relationship} currentPartnerId={currentPartner?.id} />
          ) : null;
        break;
      case RelationshipStatus.IN_REVIEW:
        slideOverContent =
          relationship.receiver_partner.id === currentPartner?.id ? (
            <TestMessagePendingReview relationship={relationship} currentPartnerId={currentPartner?.id} />
          ) : null;
        break;
    }
  }

  useEffect(() => {
    if (open && !slideOverContent && !isLoading && relationshipId) {
      navigate(routeTo('relationshipDetails', { partnerId: partnerId!, relationshipId: relationshipId! }), {
        replace: true,
      });
    }
  }, [open, isLoading, navigate, partnerId, relationshipId, routeTo, slideOverContent]);

  return (
    <SlideOver
      size="large"
      open={open}
      setOpen={() =>
        navigate(
          routeTo('relationshipDetails', {
            partnerId: partnerId!,
            relationshipId: relationshipId!,
          }),
        )
      }
      title={isLoading ? <SlideOverTitleSkeleton /> : otherPartner?.name}
      subtitle={
        isLoading ? (
          <SlideOverSubtitleSkeleton />
        ) : (
          t('connections:connectionsTest.drawer.subtitle', {
            documentType: t(`common:messageTypes.plural.${relationship?.message_type}`),
          })
        )
      }
      headerImage={<PartnerLogo imageUrl={otherPartner?.logo_url} size="large" isLoading={isLoading} />}
    >
      {slideOverContent}
      {!isLoading && children}
    </SlideOver>
  );
};

const TestMessageRequested = ({
  relationship,
  currentPartnerId,
}: {
  relationship: RelationshipV2;
  currentPartnerId: string | undefined;
}) => {
  if (!currentPartnerId) {
    return null;
  }
  if (isReceiver({ relationship, currentPartnerId })) {
    return null;
  }

  const senderConnector =
    relationship?.sender_integration?.testing?.connector || relationship?.sender_integration?.production?.connector;

  return senderConnector === Connectors.WEB_EDI ? (
    relationship ? (
      <ConnectionsTestWebEdi relationship={relationship} />
    ) : null
  ) : (
    <ConnectionsTestNonWebEDI relationship={relationship} />
  );
};
